import type { api } from '@guild/core'

import type { App } from './app.ts'
import { inboxStore, loadInbox } from './inbox.ts'
import { letterSentOn, loadLetterSent } from './letter-sent.ts'
import { hasNotices, noteAtlas, unopenedNotices } from './notices.ts'
import type { Route } from './route.ts'
import { createStore } from './store.ts'

/**
 * The day, once, shared (#135, Q27.1).
 *
 * Every day is the same four steps in the same order -- read the post, return
 * to yesterday's sinking island, walk today's island, write the letter -- and
 * a player who did not walk yesterday's rising day starts at the third. The
 * strip under the header draws the four as a course, and the day opens on the
 * first step not done, which is #133's morning generalised: the post if
 * unopened letters wait, else yesterday's island while its return trip is
 * unspent, else today's until it is spent, else the letter until sent, else
 * today's island.
 *
 * Every state is derivable from what the client already fetches. What was
 * missing was yesterday's island, which only the island screen asked for and
 * only when routed there; it is fetched here once on boot, with the inbox,
 * and again when the island screen loads a day this store has not seen --
 * which is also when a tab left open across the 00:00 UTC job learns about
 * the letters that arrived (#116). The inbox and whether today's letter has
 * gone are read from their own stores, so opening a letter or sealing one
 * moves the strip without another ask.
 *
 * Never a clock of its own (Q17.1): today is the day the server's island
 * answers to, and yesterday is the day before that. Nothing here decides an
 * outcome; the states are read off `spent`, `returnLanded`, `opened` and
 * `lettersSent`, which the server wrote.
 */

export type DayState = {
  /** Today's island, or null until it has been asked for. */
  today: api.ApiIslandState | null
  /**
   * Yesterday's island, while there is a return trip to make: the Atlas says
   * the player walked it and it is still sinking. Null otherwise.
   */
  yesterday: api.ApiIslandState | null
  /** True when the Atlas lists yesterday's island at all. */
  walkedYesterday: boolean
  loaded: boolean
  /**
   * The day this session has already opened on its current step. Once per
   * game day, and in the store rather than in storage: a fresh session is a
   * fresh morning (#133).
   */
  openedDay: string | null
}

export const dayStore = createStore<DayState>({
  today: null,
  yesterday: null,
  walkedYesterday: false,
  loaded: false,
  openedDay: null,
})

/** The four steps, in the order a day takes them. */
export const COURSE_STEPS = ['post', 'sinking', 'rising', 'letter'] as const
export type CourseStep = (typeof COURSE_STEPS)[number]

/** The `ui` key naming each step. */
export const COURSE_STEP_KEYS: Readonly<Record<CourseStep, string>> = {
  post: 'coursePost',
  sinking: 'courseSinking',
  rising: 'courseRising',
  letter: 'courseLetter',
}

export type CourseState = 'done' | 'now' | 'later' | 'notToday'

/** The `ui` key for each state word. */
export const COURSE_STATE_KEYS: Readonly<Record<CourseState, string>> = {
  done: 'courseDone',
  now: 'courseNow',
  later: 'courseLater',
  notToday: 'courseNotToday',
}

export type Course = {
  readonly steps: readonly { readonly step: CourseStep; readonly state: CourseState }[]
  /** The first step not done, or null when the course is run. */
  readonly current: CourseStep | null
  /** True when nothing is left to do today: every step done or not today's. */
  readonly complete: boolean
  /** Yesterday's day, for the key that goes there. */
  readonly yesterday: string | null
  /** When today's island begins sinking, in unix seconds (Q30.1). */
  readonly turnsAt: number | null
  /** When yesterday's sinking island finalises; null when there is no return trip. */
  readonly goesUnderAt: number | null
}

/** The day before, as a `YYYY-MM-DD` UTC string, which is what a day is everywhere. */
export const previousDay = (day: string): string =>
  new Date(Date.parse(`${day}T00:00:00.000Z`) - 86_400_000).toISOString().slice(0, 10)

/**
 * Which player the day is about. Bumped by every clear, so an answer to the
 * last player's question is dropped rather than shown to the next (#115).
 */
let generation = 0

/**
 * The load in flight, so two askers in one boot share one set of requests --
 * and whose player it is for, so the next player's ask is not handed the
 * last one's promise (#115).
 */
let inFlight: { readonly generation: number; readonly promise: Promise<void> } | null = null

/**
 * Fill the store for the day today's island answers to.
 *
 * `today` is the island the caller has just fetched, when it has one, so the
 * island screen's own ask is not repeated. A store already holding that day
 * is patched rather than refilled; a store holding an older day -- a tab left
 * open across the 00:00 UTC job -- is refilled, which is the one signal that
 * a new day has come (#116). Nothing is asked on the practice shore, which
 * shows no real course (#134).
 */
export async function loadDay(app: App, today?: api.ApiIslandState): Promise<void> {
  if (app.practice !== null) return
  const state = dayStore.get()
  if (today !== undefined && state.loaded && state.today?.day === today.day) {
    noteIsland(today)
    return
  }
  if (inFlight !== null && inFlight.generation === generation) return inFlight.promise
  const mine = generation
  const promise = fill(app, today).finally(() => {
    if (inFlight?.generation === mine) inFlight = null
  })
  inFlight = { generation: mine, promise }
  return promise
}

async function fill(app: App, given?: api.ApiIslandState): Promise<void> {
  const mine = generation
  // The post is asked for on every fill: the first step of the day is read
  // off it, and a fill is a new day, on which the post has changed.
  const [, result] = await Promise.all([
    loadInbox(app),
    given === undefined ? app.api.island() : Promise.resolve({ ok: true as const, value: given }),
  ])
  if (!result.ok || generation !== mine) return
  const today = result.value

  const yesterday = previousDay(today.day)
  const atlas = await app.api.atlas()
  if (generation !== mine) return
  const walked = atlas.ok ? atlas.value.islands.find((entry) => entry.day === yesterday) : undefined
  // The same Atlas says which islands went under lately: the notices from
  // the Keeper of the Rolls (Q32.2).
  if (atlas.ok) noteAtlas(app, atlas.value, today.day)

  let sinking: api.ApiIslandState | null = null
  if (walked?.status === 'sinking') {
    const result = await app.api.island(yesterday)
    if (generation !== mine) return
    if (result.ok && result.value.budget === 'return') sinking = result.value
  }

  // Shared with the island and the letter, which ask the same question (#102).
  await loadLetterSent(app, today.day)
  if (generation !== mine) return

  dayStore.set({
    today,
    yesterday: sinking,
    walkedYesterday: walked !== undefined,
    loaded: true,
  })
}

/**
 * A fresher copy of an island the store holds: after a step, a landing, or
 * a visit. The strip follows `spent` and `returnLanded` as they change, so
 * a day walked out turns its step to done without another ask.
 */
export function noteIsland(island: api.ApiIslandState): void {
  const state = dayStore.get()
  if (!state.loaded) return
  if (state.today?.day === island.day) dayStore.set({ today: island })
  else if (state.yesterday?.day === island.day) dayStore.set({ yesterday: island })
}

/** Forget the day when the player changes: their morning is not the next one's. */
export function clearDay(): void {
  generation += 1
  dayStore.reset()
}

/**
 * The four steps and their states, or null before the day is known.
 *
 * Each step is first done, pending or not today's; the first pending step is
 * the current one, and every pending step after it is later. That is the
 * whole rule, and the opening of the day is the same rule read once.
 */
export function courseOf(): Course | null {
  const state = dayStore.get()
  if (!state.loaded || state.today === null) return null
  const today = state.today
  const yesterday = previousDay(today.day)

  const pending = (step: CourseStep): boolean | 'notToday' => {
    switch (step) {
      case 'post': {
        // The post is about yesterday's island, and about the islands that
        // went under lately: unopened letters about the one, and unopened
        // notices from the Rolls about the others (Q32.2), are the morning's
        // reading; none, or all opened, is a post read.
        if (!state.walkedYesterday && !hasNotices()) return 'notToday'
        if (unopenedNotices() > 0) return true
        return inboxStore
          .get()
          .letters.some((letter) => letter.dayAbout === yesterday && !letter.opened)
      }
      case 'sinking':
        // Only while there is a return trip: walked yesterday, still sinking,
        // and its return budget not spent. Landing counts as part of it.
        if (state.yesterday === null) return 'notToday'
        return !state.yesterday.returnLanded || !state.yesterday.spent
      case 'rising':
        return !today.spent
      case 'letter':
        return !letterSentOn(today.day)
    }
  }

  const steps: { step: CourseStep; state: CourseState }[] = []
  let current: CourseStep | null = null
  for (const step of COURSE_STEPS) {
    const left = pending(step)
    if (left === 'notToday') steps.push({ step, state: 'notToday' })
    else if (!left) steps.push({ step, state: 'done' })
    else if (current === null) {
      current = step
      steps.push({ step, state: 'now' })
    } else steps.push({ step, state: 'later' })
  }

  return {
    steps,
    current,
    complete: current === null,
    yesterday: state.yesterday?.day ?? null,
    turnsAt: today.turnsAt,
    goesUnderAt: state.yesterday?.turnsAt ?? null,
  }
}

/** Where each step's key goes. */
export function routeForStep(step: CourseStep, course: Course): Route {
  switch (step) {
    case 'post':
      return { screen: 'post', publicId: null }
    case 'sinking':
      return { screen: 'island', day: course.yesterday }
    case 'rising':
      return { screen: 'island', day: null }
    case 'letter':
      return { screen: 'letter' }
  }
}

/**
 * Open the day, once: the step the first visit of a game day lands on.
 *
 * Returns the current step the first time it is asked on a day, and null
 * ever after -- and null when the course is already run, which lands on
 * today's island as any other visit does. The caller routes; this only
 * decides, from what the store holds.
 */
export function openDay(): CourseStep | null {
  const state = dayStore.get()
  const course = courseOf()
  if (course === null || state.today === null) return null
  if (state.openedDay === state.today.day) return null
  dayStore.set({ openedDay: state.today.day })
  return course.current
}
